import { createApi } from "@reduxjs/toolkit/query/react";

import { triggerFileDownload } from "@/shared/helpers/trigger-file-download.helper";
import { type IApiResponse } from "@/shared/interfaces/api-response.interface";

import { ApiTags } from "../api-tags.enum";
import customFetchBase from "../custom-fetch-base";

import {
  fetchReportExport,
  fetchCustomReportExport,
  fetchStudentListReportExport,
  fetchAiReportExport,
} from "./functions";
import { fetchFoundersLetterQrDataUrl } from "./report-export.helpers";
import {
  type IExportReportArg,
  type IExportCustomReportArg,
  type IExportReportSuccessResult,
  type IReportJobStatusResponse,
  type ISystemUserReportsResponse,
  type ISystemUserReportResponse,
  type SavedSystemUserReportScope,
  type ISchoolsResponse,
  type ICheckDataSufficiencyResponse,
  type IProjectionBaselineYearsResponse,
  type IAvailableProjectionsResponse,
  type IAvailableHistoricalEnrollmentResponse,
  type IProjectionFeatureStatusResponse,
  type IProjectionApisResponse,
  type IDistrictProjectionDefaultResponse,
  type IDistrictMlTrainingStatusResponse,
  type INewHousingProjectsReportResponse,
  type IAggregatedProjectionResponse,
  type IGetAggregatedReportArg,
  type IExecutiveSummaryChoicePhaseResponse,
  type IExecutiveSummaryHistoryPhaseResponse,
  type IExecutiveSummaryHousingPhaseResponse,
  type IExecutiveSummaryInfraPhaseResponse,
  type IExecutiveSummaryMetaPhaseResponse,
  type IExecutiveSummaryProjectionPhaseResponse,
  type IExecutiveSummaryProjectionReviewStatusResponse,
  type IGetExecutiveSummaryReportArg,
  type IGetExecutiveSummaryProjectionReviewArg,
  type IRecordExecutiveSummaryProjectionReviewRequest,
  type ISkipAllExecutiveSummaryProjectionReviewRequest,
  type ISaveExecutiveSummaryReportRequest,
  type IFoundersLetterQrResponse,
  type ProjectionTypeEnum,
  type ReportValueTypeEnum,
  type SchoolTypeEnum,
  type IAiReportResponse,
  type ICustomReportResponse,
  type CustomReportScope,
  type IStudentListPreviewRequest,
  type IExportStudentListReportRequest,
  type IStudentListPreviewResponse,
  type IStudentListPreviewStudentsRequest,
  type IStudentListPreviewStudentsResponse,
  type IGenerateStudentListReportResponse,
  type IGetStudentListReportStudentsArg,
  type IStudentListReportResponse,
  type IStudentListReportStudentsResponse,
} from "./reports.interface";
import { SystemUserReportTypeEnum } from "./system-user-report-type.enum";

/** Shared list tag for workspace occupancy report (`GET /projects/reports/new-housing-projects`). */
export const NEW_HOUSING_PROJECTS_REPORT_TAG = {
  type: ApiTags.Reports,
  id: "NEW_HOUSING_PROJECTS",
} as const;

/** Cohort query params shared by the Executive Summary phase GETs. */
const buildExecutiveSummaryCohortParams = ({ districtId, year, isLegacy }: IGetExecutiveSummaryReportArg) => ({
  districtId,
  ...(year != null ? { year } : {}),
  ...(typeof isLegacy === "boolean" ? { isLegacy } : {}),
});

const reportsApi = createApi({
  reducerPath: "reportsApi",
  baseQuery: customFetchBase,
  tagTypes: [ApiTags.Reports, ApiTags.ExecutiveSummary],
  endpoints: (builder) => ({
    getReportJobStatus: builder.query<IReportJobStatusResponse[], { districtId: string }>({
      query: ({ districtId }) => ({
        url: "/report/jobs/statuses",
        method: "GET",
        params: { districtId },
      }),
      providesTags: [ApiTags.Reports],
      transformResponse: (result: IApiResponse<IReportJobStatusResponse[]>) => result.data,
    }),
    getSavedReports: builder.query<
      ISystemUserReportsResponse,
      { districtId: string; scope?: SavedSystemUserReportScope }
    >({
      query: ({ districtId, scope }) => ({
        url: "/report/saved",
        method: "GET",
        params: { districtId, ...(scope ? { scope } : {}) },
      }),
      providesTags: [ApiTags.Reports],
      transformResponse: (result: IApiResponse<ISystemUserReportsResponse>) => result.data,
    }),
    getSavedReportById: builder.query<ISystemUserReportResponse, string>({
      query: (id) => ({
        url: `/report/saved/${id}`,
        method: "GET",
      }),
      providesTags: [ApiTags.Reports],
      transformResponse: (result: IApiResponse<ISystemUserReportResponse>) => result.data,
    }),
    updateSavedReportMetadata: builder.mutation<
      ISystemUserReportResponse,
      {
        reportId: string;
        name?: string;
        desc?: string | null;
        visibility?: "PRIVATE" | "SHARED";
      }
    >({
      query: ({ reportId, ...body }) => ({
        url: `/report/saved/${reportId}/metadata`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [ApiTags.Reports],
      transformResponse: (result: IApiResponse<ISystemUserReportResponse>) => result.data,
    }),
    getAiReportById: builder.query<IAiReportResponse, string>({
      query: (id) => ({
        url: `/report/ai/${id}`,
        method: "GET",
      }),
      providesTags: [ApiTags.Reports],
      transformResponse: (result: IApiResponse<IAiReportResponse>) => result.data,
    }),
    getAiReportTablePage: builder.query<
      IAiReportResponse,
      { reportId: string; offset: number; limit: number }
    >({
      query: ({ reportId, offset, limit }) => ({
        url: `/report/ai/${reportId}`,
        method: "GET",
        params: { offset, limit },
      }),
      // Snapshot rows do not change; skip Reports-tag invalidation so a workspace refresh
      // does not re-load the stored JSON for every open chat table page.
      keepUnusedDataFor: 300,
      transformResponse: (result: IApiResponse<IAiReportResponse>) => result.data,
    }),
    exportAiReport: builder.mutation<IExportReportSuccessResult, string>({
      queryFn: async (reportId) => {
        const result = await fetchAiReportExport(reportId);
        if (result.error) {
          return { error: { status: result.error.statusCode, data: result.error } };
        }
        triggerFileDownload(result.data!.blob, result.data!.filename);
        return {
          data: { success: true as const, filename: result.data!.filename },
        };
      },
    }),
    getCustomReportByReportId: builder.query<ICustomReportResponse | null, string>({
      query: (reportId) => ({
        url: `/report/custom/by-report/${reportId}`,
        method: "GET",
      }),
      providesTags: [ApiTags.Reports],
      transformResponse: (result: IApiResponse<ICustomReportResponse | null>) => result.data ?? null,
    }),
    getCustomReports: builder.query<
      ICustomReportResponse[],
      { districtId: string; scope: CustomReportScope }
    >({
      query: ({ districtId, scope }) => ({
        url: "/report/custom",
        method: "GET",
        params: { districtId, scope },
      }),
      providesTags: [ApiTags.Reports],
      transformResponse: (result: IApiResponse<ICustomReportResponse[]>) => result.data ?? [],
    }),
    saveCustomReport: builder.mutation<
      ICustomReportResponse,
      {
        districtId: string;
        reportId: string;
        name: string;
        filters?: unknown;
      }
    >({
      query: (body) => ({
        url: "/report/custom",
        method: "POST",
        body,
      }),
      invalidatesTags: [ApiTags.Reports],
      transformResponse: (result: IApiResponse<ICustomReportResponse>) => result.data,
    }),
    deleteCustomReport: builder.mutation<void, string>({
      query: (customReportId) => ({
        url: `/report/custom/${customReportId}`,
        method: "DELETE",
      }),
      invalidatesTags: [ApiTags.Reports],
    }),
    deleteSavedReport: builder.mutation<void, string>({
      query: (reportId) => ({
        url: `/report/saved/${reportId}`,
        method: "DELETE",
      }),
      invalidatesTags: [ApiTags.Reports],
    }),
    updateCustomReportMetadata: builder.mutation<
      ICustomReportResponse,
      {
        customReportId: string;
        name?: string;
        description?: string | null;
        visibility?: "PRIVATE" | "SHARED";
      }
    >({
      query: ({ customReportId, ...body }) => ({
        url: `/report/custom/${customReportId}/metadata`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [ApiTags.Reports],
      transformResponse: (result: IApiResponse<ICustomReportResponse>) => result.data,
    }),
    saveAggregatedReport: builder.mutation<
      ISystemUserReportResponse,
      {
        districtId: string;
        reportType: ProjectionTypeEnum;
        valueType: ReportValueTypeEnum;
        schoolIds?: string[];
        schoolLevel?: SchoolTypeEnum;
        type?: SystemUserReportTypeEnum;
        year?: number;
        name?: string;
        isLegacy?: boolean;
      }
    >({
      query: (body) => ({
        url: "/report/aggregated",
        method: "POST",
        body: {
          ...body,
          type: body.type ?? SystemUserReportTypeEnum.PROJECTION,
        },
      }),
      invalidatesTags: [ApiTags.Reports],
      transformResponse: (result: IApiResponse<ISystemUserReportResponse>) => result.data,
    }),
    getAggregatedReport: builder.query<IAggregatedProjectionResponse, IGetAggregatedReportArg>({
      query: ({ districtId, reportType, valueType, year, schoolIds, schoolLevel, type, isLegacy }) => ({
        url: "/report/aggregated",
        method: "GET",
        params: {
          districtId,
          reportType,
          valueType,
          ...(year != null ? { year } : {}),
          ...(schoolIds?.length ? { schoolIds } : {}),
          ...(schoolLevel ? { schoolLevel } : {}),
          ...(type ? { type } : {}),
          ...(typeof isLegacy === "boolean" ? { isLegacy } : {}),
        },
      }),
      providesTags: [ApiTags.Reports],
      transformResponse: (result: IApiResponse<IAggregatedProjectionResponse>) => result.data,
    }),
    getExecutiveSummaryMeta: builder.query<
      IExecutiveSummaryMetaPhaseResponse,
      IGetExecutiveSummaryReportArg
    >({
      query: (arg) => ({
        url: "/report/executive-summary/meta",
        method: "GET",
        params: buildExecutiveSummaryCohortParams(arg),
      }),
      providesTags: [ApiTags.ExecutiveSummary],
      transformResponse: (result: IApiResponse<IExecutiveSummaryMetaPhaseResponse>) => result.data,
    }),
    getExecutiveSummaryProjection: builder.query<
      IExecutiveSummaryProjectionPhaseResponse,
      IGetExecutiveSummaryReportArg
    >({
      query: (arg) => ({
        url: "/report/executive-summary/projection",
        method: "GET",
        params: buildExecutiveSummaryCohortParams(arg),
      }),
      providesTags: [ApiTags.ExecutiveSummary],
      transformResponse: (result: IApiResponse<IExecutiveSummaryProjectionPhaseResponse>) => result.data,
    }),
    getExecutiveSummaryHistory: builder.query<
      IExecutiveSummaryHistoryPhaseResponse,
      IGetExecutiveSummaryReportArg
    >({
      query: (arg) => ({
        url: "/report/executive-summary/history",
        method: "GET",
        params: buildExecutiveSummaryCohortParams(arg),
      }),
      providesTags: [ApiTags.ExecutiveSummary],
      transformResponse: (result: IApiResponse<IExecutiveSummaryHistoryPhaseResponse>) => result.data,
    }),
    getExecutiveSummaryHousing: builder.query<
      IExecutiveSummaryHousingPhaseResponse,
      IGetExecutiveSummaryReportArg
    >({
      query: (arg) => ({
        url: "/report/executive-summary/housing",
        method: "GET",
        params: buildExecutiveSummaryCohortParams(arg),
      }),
      providesTags: [ApiTags.ExecutiveSummary],
      transformResponse: (result: IApiResponse<IExecutiveSummaryHousingPhaseResponse>) => result.data,
    }),
    getExecutiveSummaryChoice: builder.query<
      IExecutiveSummaryChoicePhaseResponse,
      IGetExecutiveSummaryReportArg
    >({
      query: (arg) => ({
        url: "/report/executive-summary/choice",
        method: "GET",
        params: buildExecutiveSummaryCohortParams(arg),
      }),
      providesTags: [ApiTags.ExecutiveSummary],
      transformResponse: (result: IApiResponse<IExecutiveSummaryChoicePhaseResponse>) => result.data,
    }),
    getExecutiveSummaryInfra: builder.query<
      IExecutiveSummaryInfraPhaseResponse,
      IGetExecutiveSummaryReportArg
    >({
      query: (arg) => ({
        url: "/report/executive-summary/infra",
        method: "GET",
        params: buildExecutiveSummaryCohortParams(arg),
      }),
      providesTags: [ApiTags.ExecutiveSummary],
      transformResponse: (result: IApiResponse<IExecutiveSummaryInfraPhaseResponse>) => result.data,
    }),
    getFoundersLetterQr: builder.query<IFoundersLetterQrResponse, void>({
      queryFn: async () => {
        const result = await fetchFoundersLetterQrDataUrl();
        if (result.error) {
          return { error: { status: result.error.statusCode, data: result.error } };
        }
        return { data: result.data! };
      },
      // Static About Us QR — cache for the session; no Reports-tag invalidation.
      keepUnusedDataFor: 3600,
    }),
    getExecutiveSummaryProjectionReview: builder.query<
      IExecutiveSummaryProjectionReviewStatusResponse,
      IGetExecutiveSummaryProjectionReviewArg
    >({
      query: ({ districtId, year, isLegacy }) => ({
        url: "/report/executive-summary/projection-review",
        method: "GET",
        params: {
          districtId,
          ...(year != null ? { year } : {}),
          ...(typeof isLegacy === "boolean" ? { isLegacy } : {}),
        },
      }),
      providesTags: [ApiTags.Reports],
      transformResponse: (result: IApiResponse<IExecutiveSummaryProjectionReviewStatusResponse>) =>
        result.data,
    }),
    recordExecutiveSummaryProjectionReview: builder.mutation<
      IExecutiveSummaryProjectionReviewStatusResponse,
      IRecordExecutiveSummaryProjectionReviewRequest
    >({
      query: (body) => ({
        url: "/report/executive-summary/projection-review",
        method: "POST",
        body,
      }),
      invalidatesTags: [ApiTags.Reports, ApiTags.ExecutiveSummary],
      transformResponse: (result: IApiResponse<IExecutiveSummaryProjectionReviewStatusResponse>) =>
        result.data,
    }),
    skipAllExecutiveSummaryProjectionReview: builder.mutation<
      IExecutiveSummaryProjectionReviewStatusResponse,
      ISkipAllExecutiveSummaryProjectionReviewRequest
    >({
      query: (body) => ({
        url: "/report/executive-summary/projection-review/skip-all",
        method: "POST",
        body,
      }),
      invalidatesTags: [ApiTags.Reports, ApiTags.ExecutiveSummary],
      transformResponse: (result: IApiResponse<IExecutiveSummaryProjectionReviewStatusResponse>) =>
        result.data,
    }),
    saveExecutiveSummary: builder.mutation<ISystemUserReportResponse, ISaveExecutiveSummaryReportRequest>({
      query: (body) => ({
        url: "/report/executive-summary",
        method: "POST",
        body,
      }),
      invalidatesTags: [ApiTags.Reports],
      transformResponse: (result: IApiResponse<ISystemUserReportResponse>) => result.data,
    }),
    getSchools: builder.query<
      ISchoolsResponse,
      { districtId: string; schoolType?: SchoolTypeEnum | "all" }
    >({
      query: ({ districtId, schoolType }) => ({
        url: "/report/schools",
        method: "GET",
        params: {
          districtId,
          ...(schoolType && schoolType !== "all" && { schoolType }),
        },
      }),
      providesTags: [ApiTags.Reports],
      transformResponse: (result: IApiResponse<ISchoolsResponse>) => result.data,
    }),
    checkDataSufficiency: builder.query<ICheckDataSufficiencyResponse, { districtId: string }>({
      query: ({ districtId }) => ({
        url: "/report/data-sufficiency",
        method: "GET",
        params: { districtId },
      }),
      providesTags: [ApiTags.Reports],
      transformResponse: (result: IApiResponse<ICheckDataSufficiencyResponse>) => result.data,
    }),
    startReportGeneration: builder.mutation<void, { districtId: string; includeHybrid?: boolean }>({
      query: (body) => ({
        url: "/report/generate",
        method: "POST",
        body,
      }),
      invalidatesTags: [ApiTags.Reports, ApiTags.ExecutiveSummary],
    }),
    previewStudentListReport: builder.mutation<IStudentListPreviewResponse, IStudentListPreviewRequest>({
      query: (body) => ({
        url: "/report/student-list/preview",
        method: "POST",
        body,
      }),
      transformResponse: (result: IApiResponse<IStudentListPreviewResponse>) => result.data,
    }),
    previewStudentListReportStudents: builder.mutation<
      IStudentListPreviewStudentsResponse,
      IStudentListPreviewStudentsRequest
    >({
      query: (body) => ({
        url: "/report/student-list/preview/students",
        method: "POST",
        body,
      }),
      transformResponse: (result: IApiResponse<IStudentListPreviewStudentsResponse>) => result.data,
    }),
    generateStudentListReport: builder.mutation<
      IGenerateStudentListReportResponse,
      IStudentListPreviewRequest
    >({
      query: (body) => ({
        url: "/report/student-list/generate",
        method: "POST",
        body,
      }),
      invalidatesTags: [ApiTags.Reports],
      transformResponse: (result: IApiResponse<IGenerateStudentListReportResponse>) => result.data,
    }),
    getStudentListReportById: builder.query<IStudentListReportResponse, string>({
      query: (reportId) => ({
        url: `/report/student-list/${reportId}`,
        method: "GET",
      }),
      providesTags: [ApiTags.Reports],
      transformResponse: (result: IApiResponse<IStudentListReportResponse>) => result.data,
    }),
    getStudentListReportStudents: builder.query<
      IStudentListReportStudentsResponse,
      IGetStudentListReportStudentsArg
    >({
      query: ({ reportId, cursor, limit }) => ({
        url: `/report/student-list/${reportId}/students`,
        method: "GET",
        params: {
          ...(cursor ? { cursor } : {}),
          ...(limit != null ? { limit } : {}),
        },
      }),
      providesTags: [ApiTags.Reports],
      transformResponse: (result: IApiResponse<IStudentListReportStudentsResponse>) => result.data,
    }),
    getAvailableProjections: builder.query<IAvailableProjectionsResponse, { districtId: string }>({
      query: ({ districtId }) => ({
        url: "/report/projections/available",
        method: "GET",
        params: { districtId },
      }),
      providesTags: [ApiTags.Reports],
      transformResponse: (result: IApiResponse<IAvailableProjectionsResponse>) => result.data,
    }),
    getProjectionBaselineYears: builder.query<
      IProjectionBaselineYearsResponse,
      { districtId: string; isLegacy?: boolean }
    >({
      query: ({ districtId, isLegacy }) => ({
        url: "/report/projections/baseline-years",
        method: "GET",
        params: { districtId, ...(typeof isLegacy === "boolean" ? { isLegacy } : {}) },
      }),
      providesTags: [ApiTags.Reports],
      transformResponse: (result: IApiResponse<IProjectionBaselineYearsResponse>) => result.data,
    }),
    getAvailableHistoricalEnrollment: builder.query<
      IAvailableHistoricalEnrollmentResponse,
      { districtId: string }
    >({
      query: ({ districtId }) => ({
        url: "/report/historical/available",
        method: "GET",
        params: { districtId },
      }),
      providesTags: [ApiTags.Reports],
      transformResponse: (result: IApiResponse<IAvailableHistoricalEnrollmentResponse>) => result.data,
    }),
    getProjectionFeatureStatus: builder.query<IProjectionFeatureStatusResponse, { districtId: string }>({
      query: ({ districtId }) => ({
        url: "/report/features/projection-reports",
        method: "GET",
        params: { districtId },
      }),
      providesTags: [ApiTags.Reports],
      transformResponse: (result: IApiResponse<IProjectionFeatureStatusResponse>) => result.data,
    }),
    toggleProjectionFeatureStatus: builder.mutation<
      IProjectionFeatureStatusResponse,
      { districtId: string; enabled: boolean }
    >({
      query: (body) => ({
        url: "/report/features/projection-reports",
        method: "POST",
        body,
      }),
      invalidatesTags: [ApiTags.Reports, ApiTags.ExecutiveSummary],
      transformResponse: (result: IApiResponse<IProjectionFeatureStatusResponse>) => result.data,
    }),
    getProjectionApis: builder.query<IProjectionApisResponse, { districtId: string }>({
      query: ({ districtId }) => ({
        url: "/report/projection-apis",
        method: "GET",
        params: { districtId },
      }),
      providesTags: [ApiTags.Reports],
      transformResponse: (result: IApiResponse<IProjectionApisResponse>) => result.data,
    }),
    getDistrictProjectionDefault: builder.query<IDistrictProjectionDefaultResponse, { districtId: string }>({
      query: ({ districtId }) => ({
        url: `/report/districts/${districtId}/projection-default`,
        method: "GET",
      }),
      providesTags: [ApiTags.Reports],
      transformResponse: (result: IApiResponse<IDistrictProjectionDefaultResponse>) => result.data,
    }),
    setDistrictProjectionDefault: builder.mutation<void, { districtId: string; defaultIsLegacy: boolean }>({
      query: ({ districtId, defaultIsLegacy }) => ({
        url: `/report/districts/${districtId}/projection-default`,
        method: "PUT",
        body: { defaultIsLegacy },
      }),
      invalidatesTags: [ApiTags.Reports, ApiTags.ExecutiveSummary],
    }),
    restartProjectionStep: builder.mutation<void, { stepId: string }>({
      query: ({ stepId }) => ({
        url: `/report/jobs/projection-steps/${stepId}/restart`,
        method: "POST",
      }),
      invalidatesTags: [ApiTags.Reports, ApiTags.ExecutiveSummary],
    }),
    getDistrictMlTrainingStatus: builder.query<IDistrictMlTrainingStatusResponse, { districtId: string }>({
      query: ({ districtId }) => ({
        url: `/report/districts/${districtId}/training-status`,
        method: "GET",
      }),
      providesTags: [ApiTags.Reports],
      transformResponse: (result: IApiResponse<IDistrictMlTrainingStatusResponse>) => result.data,
    }),
    startDistrictMlTraining: builder.mutation<void, { districtId: string; reportsOnly?: boolean }>({
      query: ({ districtId, reportsOnly }) => ({
        url: `/report/districts/${districtId}/training-runs`,
        method: "POST",
        body: { reportsOnly: reportsOnly ?? false },
      }),
      invalidatesTags: [ApiTags.Reports, ApiTags.ExecutiveSummary],
    }),
    getNewHousingProjectsReport: builder.query<
      INewHousingProjectsReportResponse,
      { districtId: string }
    >({
      query: ({ districtId }) => ({
        url: "/projects/reports/new-housing-projects",
        method: "GET",
        params: { districtId },
      }),
      providesTags: [NEW_HOUSING_PROJECTS_REPORT_TAG],
      transformResponse: (result: IApiResponse<INewHousingProjectsReportResponse>) => result.data,
    }),
    exportReport: builder.mutation<IExportReportSuccessResult, IExportReportArg>({
      queryFn: async (arg) => {
        const result = await fetchReportExport(arg);
        if (result.error) {
          return { error: { status: result.error.statusCode, data: result.error } };
        }
        triggerFileDownload(result.data!.blob, result.data!.filename);
        return {
          data: { success: true as const, filename: result.data!.filename },
        };
      },
    }),
    exportCustomReport: builder.mutation<IExportReportSuccessResult, IExportCustomReportArg>({
      queryFn: async (arg) => {
        const result = await fetchCustomReportExport(arg);
        if (result.error) {
          return { error: { status: result.error.statusCode, data: result.error } };
        }
        triggerFileDownload(result.data!.blob, result.data!.filename);
        return {
          data: { success: true as const, filename: result.data!.filename },
        };
      },
    }),
    exportStudentListReport: builder.mutation<IExportReportSuccessResult, IExportStudentListReportRequest>({
      queryFn: async (arg) => {
        const result = await fetchStudentListReportExport(arg);
        if (result.error) {
          return { error: { status: result.error.statusCode, data: result.error } };
        }
        triggerFileDownload(result.data!.blob, result.data!.filename);
        return {
          data: { success: true as const, filename: result.data!.filename },
        };
      },
    }),
  }),
});

export const {
  useGetReportJobStatusQuery,
  useGetSavedReportsQuery,
  useGetSavedReportByIdQuery,
  useUpdateSavedReportMetadataMutation,
  useGetAiReportByIdQuery,
  useGetAiReportTablePageQuery,
  useExportAiReportMutation,
  useGetCustomReportByReportIdQuery,
  useGetCustomReportsQuery,
  useSaveCustomReportMutation,
  useDeleteCustomReportMutation,
  useDeleteSavedReportMutation,
  useUpdateCustomReportMetadataMutation,
  useSaveAggregatedReportMutation,
  useGetAggregatedReportQuery,
  useGetExecutiveSummaryMetaQuery,
  useGetExecutiveSummaryProjectionQuery,
  useGetExecutiveSummaryHistoryQuery,
  useGetExecutiveSummaryHousingQuery,
  useGetExecutiveSummaryChoiceQuery,
  useGetExecutiveSummaryInfraQuery,
  useGetFoundersLetterQrQuery,
  useGetExecutiveSummaryProjectionReviewQuery,
  useLazyGetExecutiveSummaryProjectionReviewQuery,
  useRecordExecutiveSummaryProjectionReviewMutation,
  useSkipAllExecutiveSummaryProjectionReviewMutation,
  useSaveExecutiveSummaryMutation,
  useGetSchoolsQuery,
  useCheckDataSufficiencyQuery,
  useStartReportGenerationMutation,
  usePreviewStudentListReportMutation,
  usePreviewStudentListReportStudentsMutation,
  useGenerateStudentListReportMutation,
  useGetStudentListReportByIdQuery,
  useLazyGetStudentListReportStudentsQuery,
  useGetStudentListReportStudentsQuery,
  useGetAvailableProjectionsQuery,
  useGetProjectionBaselineYearsQuery,
  useGetAvailableHistoricalEnrollmentQuery,
  useGetProjectionFeatureStatusQuery,
  useToggleProjectionFeatureStatusMutation,
  useGetProjectionApisQuery,
  useGetDistrictProjectionDefaultQuery,
  useSetDistrictProjectionDefaultMutation,
  useRestartProjectionStepMutation,
  useGetDistrictMlTrainingStatusQuery,
  useStartDistrictMlTrainingMutation,
  useGetNewHousingProjectsReportQuery,
  useLazyGetNewHousingProjectsReportQuery,
  useExportReportMutation,
  useExportCustomReportMutation,
  useExportStudentListReportMutation,
} = reportsApi;

export { reportsApi };
export default reportsApi;
